import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BrokerMarketing = ({ apiBase }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showAutoMarketModal, setShowAutoMarketModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    campaign_name: '',
    project_id: '',
    campaign_type: 'project_launch',
    target_audience: 'all_brokers',
    subject: '',
    message_template: ''
  });
  const [autoMarketForm, setAutoMarketForm] = useState({
    project_ids: [],
    template_id: '',
    send_method: 'email'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [campaignsRes, templatesRes, projectsRes, brokersRes] = await Promise.all([
        axios.get(`${apiBase}/broker-marketing/campaigns`),
        axios.get(`${apiBase}/broker-marketing/templates`),
        axios.get(`${apiBase}/projects`),
        axios.get(`${apiBase}/channel-partners`)
      ]);

      setCampaigns(campaignsRes.data);
      setTemplates(templatesRes.data);
      setProjects(projectsRes.data);
      setBrokers(brokersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${apiBase}/broker-marketing/campaigns`, {
        ...campaignForm,
        created_by: 1
      });
      alert('Campaign created successfully!');
      setShowCampaignModal(false);
      setCampaignForm({
        campaign_name: '',
        project_id: '',
        campaign_type: 'project_launch',
        target_audience: 'all_brokers',
        subject: '',
        message_template: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating campaign:', error);
      alert('Error creating campaign: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('Send this campaign to all active brokers?')) return;

    setLoading(true);
    try {
      const response = await axios.post(`${apiBase}/broker-marketing/campaigns/${campaignId}/send`, {
        send_method: 'email'
      });
      alert(`Campaign sent to ${response.data.sent_count} brokers!`);
      fetchData();
    } catch (error) {
      console.error('Error sending campaign:', error);
      alert('Error sending campaign: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAutoMarket = async (e) => {
    e.preventDefault();
    if (!window.confirm('This will automatically market all selected projects to all active brokers. Continue?')) return;

    setLoading(true);
    try {
      const response = await axios.post(`${apiBase}/broker-marketing/auto-market`, autoMarketForm);
      alert(`Auto-marketing completed!\n\n${response.data.projects_marketed} project(s) marketed to ${response.data.total_brokers} brokers.`);
      setShowAutoMarketModal(false);
      setAutoMarketForm({
        project_ids: [],
        template_id: '',
        send_method: 'email'
      });
      fetchData();
    } catch (error) {
      console.error('Error in auto-marketing:', error);
      alert('Error in auto-marketing: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === parseInt(templateId));
    if (template) {
      setCampaignForm({
        ...campaignForm,
        subject: template.subject,
        message_template: template.message_body
      });
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1>Real Connect Ecosystem - Broker Marketing</h1>
          <div style={{ marginTop: '8px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <a 
              href="https://www.therealconnect.in" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#2563eb', textDecoration: 'none', fontSize: '14px', fontWeight: '500' }}
            >
              🌐 Visit The Real Connect
            </a>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => setShowAutoMarketModal(true)}
          >
            🚀 Auto-Market All Projects
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowCampaignModal(true)}
          >
            + Create Campaign
          </button>
        </div>
      </div>

      {/* Real Connect Info */}
      <div style={{ marginBottom: '24px', padding: '16px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ margin: '0 0 8px 0', color: '#1e40af', fontSize: '18px' }}>The Real Connect Ecosystem</h3>
            <p style={{ margin: '0', color: '#1e40af', fontSize: '14px' }}>
              Automated broker marketing platform for real estate projects
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <a 
              href="https://www.therealconnect.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ textDecoration: 'none' }}
            >
              Visit The Real Connect →
            </a>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: '24px' }}>
        <div className="stat-card">
          <h3>{campaigns.length}</h3>
          <p>Total Campaigns</p>
        </div>
        <div className="stat-card">
          <h3>{brokers.length}</h3>
          <p>Active Brokers</p>
        </div>
        <div className="stat-card">
          <h3>{projects.length}</h3>
          <p>Active Projects</p>
        </div>
        <div className="stat-card">
          <h3>{templates.length}</h3>
          <p>Email Templates</p>
        </div>
      </div>

      {/* Campaigns List */}
      <div className="card">
        <h2 className="card-title">Marketing Campaigns</h2>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Campaign Name</th>
                <th>Project</th>
                <th>Type</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                    No campaigns found. Create your first campaign!
                  </td>
                </tr>
              ) : (
                campaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td>
                      <strong>{campaign.campaign_name}</strong>
                    </td>
                    <td>{campaign.project_name || '-'}</td>
                    <td>
                      <span className="badge" style={{
                        backgroundColor: '#2563eb20',
                        color: '#2563eb'
                      }}>
                        {campaign.campaign_type.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <span className="badge" style={{
                        backgroundColor: campaign.status === 'sent' ? '#10b98120' : 
                                         campaign.status === 'draft' ? '#f59e0b20' : '#6b728020',
                        color: campaign.status === 'sent' ? '#10b981' : 
                               campaign.status === 'draft' ? '#f59e0b' : '#6b7280'
                      }}>
                        {campaign.status}
                      </span>
                    </td>
                    <td>{new Date(campaign.created_at).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {campaign.status === 'draft' && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleSendCampaign(campaign.id)}
                          >
                            Send
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            setSelectedCampaign(campaign);
                            // View campaign details
                          }}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Campaign Modal */}
      {showCampaignModal && (
        <div className="modal-overlay" onClick={() => setShowCampaignModal(false)} style={{ zIndex: 10000 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', zIndex: 10001 }}>
            <div className="modal-header">
              <h2>Create Marketing Campaign</h2>
              <button className="modal-close" onClick={() => setShowCampaignModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateCampaign}>
              <div className="form-group">
                <label>Campaign Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={campaignForm.campaign_name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, campaign_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Project *</label>
                <select
                  className="form-control"
                  value={campaignForm.project_id}
                  onChange={(e) => setCampaignForm({ ...campaignForm, project_id: e.target.value })}
                  required
                >
                  <option value="">Select Project</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Campaign Type *</label>
                <select
                  className="form-control"
                  value={campaignForm.campaign_type}
                  onChange={(e) => setCampaignForm({ ...campaignForm, campaign_type: e.target.value })}
                  required
                >
                  <option value="project_launch">Project Launch</option>
                  <option value="project_update">Project Update</option>
                  <option value="commission_reminder">Commission Reminder</option>
                  <option value="collateral_share">Marketing Collateral</option>
                  <option value="auto_marketing">Auto Marketing</option>
                </select>
              </div>
              <div className="form-group">
                <label>Email Template</label>
                <select
                  className="form-control"
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                >
                  <option value="">Select Template (Optional)</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.template_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  className="form-control"
                  value={campaignForm.subject}
                  onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Message Template *</label>
                <textarea
                  className="form-control"
                  rows="10"
                  value={campaignForm.message_template}
                  onChange={(e) => setCampaignForm({ ...campaignForm, message_template: e.target.value })}
                  required
                  placeholder="Use {{variable_name}} for dynamic content. Available variables: {{broker_name}}, {{project_name}}, {{commission_structure}}, etc."
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCampaignModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Auto-Market Modal */}
      {showAutoMarketModal && (
        <div className="modal-overlay" onClick={() => setShowAutoMarketModal(false)} style={{ zIndex: 10000 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', zIndex: 10001 }}>
            <div className="modal-header">
              <h2>🚀 Auto-Market All Projects</h2>
              <button className="modal-close" onClick={() => setShowAutoMarketModal(false)}>×</button>
            </div>
            <div style={{ padding: '20px 20px 0 20px' }}>
              <div style={{ marginBottom: '20px', padding: '16px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                <p style={{ margin: '0 0 12px 0', color: '#1e40af', fontWeight: '500' }}>
                  Powered by The Real Connect Ecosystem
                </p>
                <p style={{ margin: '0 0 12px 0', color: '#1e40af', fontSize: '13px', lineHeight: '1.6' }}>
                  The Real Connect automatically markets all your projects to brokers, ensuring maximum reach and engagement.
                </p>
                <a 
                  href="https://www.therealconnect.in" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                  style={{ textDecoration: 'none', fontSize: '12px', padding: '6px 12px' }}
                >
                  Visit The Real Connect →
                </a>
              </div>
            </div>
            <form onSubmit={handleAutoMarket}>
              <div className="form-group">
                <label>Select Projects (Leave empty for all projects)</label>
                <select
                  className="form-control"
                  multiple
                  size="5"
                  value={autoMarketForm.project_ids.map(String)}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                    setAutoMarketForm({ ...autoMarketForm, project_ids: selected });
                  }}
                >
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Hold Ctrl/Cmd to select multiple. If none selected, all projects will be marketed.
                </small>
              </div>
              <div className="form-group">
                <label>Email Template *</label>
                <select
                  className="form-control"
                  value={autoMarketForm.template_id}
                  onChange={(e) => setAutoMarketForm({ ...autoMarketForm, template_id: e.target.value })}
                  required
                >
                  <option value="">Select Template</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.template_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Send Method *</label>
                <select
                  className="form-control"
                  value={autoMarketForm.send_method}
                  onChange={(e) => setAutoMarketForm({ ...autoMarketForm, send_method: e.target.value })}
                  required
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
              </div>
              <div style={{
                padding: '16px',
                background: '#f0f9ff',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <strong>⚠️ This will:</strong>
                <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                  <li>Create campaigns for selected/all projects</li>
                  <li>Send marketing emails to all active brokers</li>
                  <li>Track all sends in the system</li>
                </ul>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAutoMarketModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Marketing...' : '🚀 Start Auto-Marketing'}
                </button>
              </div>
            </form>
            <div style={{ padding: '16px 20px 20px 20px', background: '#eff6ff', borderRadius: '0 0 8px 8px', borderTop: '1px solid #bfdbfe' }}>
              <p style={{ margin: '0 0 12px 0', color: '#1e40af', fontWeight: '500', fontSize: '14px' }}>
                Learn more about The Real Connect:
              </p>
              <a 
                href="https://www.therealconnect.in" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-secondary"
                style={{ textDecoration: 'none', fontSize: '12px', padding: '6px 12px' }}
              >
                Visit www.therealconnect.in →
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrokerMarketing;

